import { Injectable } from "@angular/core";
import { ChartsDataByAccount, ChartsDataByDay, FinOpsAwsResource, FinOpsCounters, HistoricalDataByDate } from "./finops-aws-resources.model";
import { Filter } from "src/app/store/preferences/filters/preferences-filters.model";
import { DateGranularityEnum } from "src/app/shared/enum/date-granularity.enum";
import { FloorPipe } from "src/app/shared/pipe/floor.pipe";
import { FormatNumberPipe } from "src/app/shared/pipe/format-number.pipe";
import { TranslateService } from "@ngx-translate/core";
import { DateService } from "src/app/shared/service/date.service";
import { FinOpsChartHistoricalData, FinOpsChartHistoricalItem } from "src/app/shared/component/chart-finops-historical/chart-finops-historical.component";
import { ColorEnum } from "src/app/shared/enum/color.enum";
import { FinOpsChartCompareData, FinOpsChartCompareItem } from "src/app/shared/component/chart-finops-compare/chart-finops-compare.component";
import { AwsResourceTypeLabelPipe } from "src/app/shared/pipe/aws-resource-type-label.pipe";
import { AwsConfig } from "src/app/store/preferences/providers/preferences-providers.model";

export type DataForDatatableCompute = { 
    measuresDatatable: { [id: string]: FinOpsAwsResource[]; },
    filteredCounters: { [id: string]: Record<string, FinOpsCounters>; },
    previousTotalCounters: { [id: string]: FinOpsCounters; }
}

@Injectable({
    providedIn: 'root'
})
export class FinOpsAwsResourcesHelper {

    // helper: recompute gap fields from cost & previous_cost
    static mergeCounters(a: FinOpsCounters | undefined, b: FinOpsCounters): FinOpsCounters {
        if (!a) {
            // clone b, but zero out derived fields; we’ll recompute gap_* later
            return {
                prediction: b.prediction,
                previous_cost: b.previous_cost,
                cost: b.cost,
                day_ratio: b.day_ratio,
                gap_ratio: 0,
                gap_value: 0
            };
        }

        return {
            prediction: a.prediction || b.prediction,
            previous_cost: a.previous_cost + b.previous_cost,
            cost: a.cost + b.cost,
            day_ratio: a.day_ratio || b.day_ratio, // keep first non-zero (typical for same date)
            gap_ratio: 0, // recompute later
            gap_value: 0
        };
    }

    // helper: recompute gap fields from cost & previous_cost
    static finalizeGaps(c: FinOpsCounters): void {
        const gap = c.cost - c.previous_cost;
        c.gap_value = gap;
        c.gap_ratio = c.previous_cost === 0 ? 100 : (gap / c.previous_cost) * 100;
    }

    static computeDataTable(historicalDatesSelected: string[], historicalDataByGranularity: HistoricalDataByDate, filters: Filter[]) {
        // compute data for datatable
        let measuresDatatable: { [id: string]: FinOpsAwsResource[]; } = {}
        let filteredCounters: { [id: string]: Record<string, FinOpsCounters>; } = {}
        let previousTotalCounters: { [id: string]: FinOpsCounters; } = {}

        for (const date of historicalDatesSelected) {
            let datatable: FinOpsAwsResource[] = []
            
            // Add each resources to datatable array
            Object.values(historicalDataByGranularity[date]).forEach(costResource => {
                datatable.push(costResource)
            })

            // Update datatable content according to current selected filters
            filters.forEach(filter => {
                switch (filter.key) {
                    case "project":
                        datatable = datatable.filter((resource) => {
                            let projectsFound = false
                            resource.projects.forEach(project => {
                                projectsFound = projectsFound || filter.value.includes(project)
                            })
                            return projectsFound
                        })
                        break;

                    case "issuesCount":
                        datatable = datatable.filter((resource) => resource['issues'].length.toString() === filter.value)
                        break;

                    case "issuesType":
                        datatable = datatable.filter((resource) => {
                            let issuesTypeFound = false
                            resource.issues.forEach(issue => {
                                issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                            })
                            return issuesTypeFound
                        })
                        break;

                    case "tag":
                        datatable = datatable.filter((resource) => {
                            let tagFound = false
                            Object.entries(resource.tags).forEach((tag: any) => {
                                let tagKey = tag[0]
                                let tagValue = tag[1]
                                filter.value.forEach((filterValue: any) => {
                                    let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                    let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                    tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                                });
                            })
                            return tagFound
                        })
                        break;

                    case "age":
                        let unknownAge = 99999
                        let filterAge: number = unknownAge
                        if (filter.value == 'today') {
                            filterAge = 0
                        } else if (filter.value.startsWith('last_')) {
                            filterAge = filter.value.substring(5, 7)
                        }
                        datatable = datatable.filter((resource) => {
                            let ageInRange = false
                            if (filterAge == unknownAge) {
                                ageInRange = resource.age == filterAge
                            } else {
                                ageInRange = resource.age <= filterAge
                            }
                            return ageInRange
                        })
                        break;

                    case "tag_":
                        let tagKey = filter.key.substring(4)
                        if (Array.isArray(filter.value)) {
                            datatable = datatable.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                        } else {
                            datatable = datatable.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                        }
                        break;

                    default:
                        if (Array.isArray(filter.value)) {
                            datatable = datatable.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                        } else {
                            datatable = datatable.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                        }
                        break;
                }


            })
            measuresDatatable[date] = datatable

            // Update counters for the measure
            let totalCounter: Record<DateGranularityEnum, FinOpsCounters> = {
                "year": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "month": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                },
                "day": {
                    prediction: false,
                    previous_cost: 0,
                    cost: 0,
                    day_ratio: 0,
                    gap_ratio: 0,
                    gap_value: 0
                }
            }

            datatable.forEach(row => 
                Object.entries(row.costs_counters).forEach(entries => {
                    const granularity = entries[0] as DateGranularityEnum
                    const counters = entries[1]

                    totalCounter[granularity].prediction = counters.prediction
                    totalCounter[granularity].cost += Number(counters.cost)
                    totalCounter[granularity].day_ratio = Number(counters.day_ratio)
                })
            )

            Object.keys(totalCounter).forEach(granularity => {
                const _granularity: DateGranularityEnum = granularity as DateGranularityEnum
                totalCounter[_granularity].cost = totalCounter[_granularity].cost
                totalCounter[_granularity].day_ratio = totalCounter[_granularity].day_ratio

                if(Object.keys(previousTotalCounters).length > 0) {
                    totalCounter[_granularity].previous_cost = previousTotalCounters[_granularity].cost
                    let gap_value = totalCounter[_granularity].cost - totalCounter[_granularity].previous_cost
                    totalCounter[_granularity].gap_ratio = totalCounter[_granularity].previous_cost == 0 ? 100 : ((gap_value / totalCounter[_granularity].previous_cost) * 100)
                    totalCounter[_granularity].gap_value = gap_value
                }
            })
            filteredCounters[date] = totalCounter
            previousTotalCounters = totalCounter
        }

        return {
            measuresDatatable: measuresDatatable,
            filteredCounters: filteredCounters,
            previousTotalCounters: previousTotalCounters
        }
    }

    static computeDatatableGrandTotal(perAccount: Record<string,DataForDatatableCompute>) {
        const out: DataForDatatableCompute = {
            measuresDatatable: {},
            filteredCounters: {},
            previousTotalCounters: {}
        };

        for (const [, data] of Object.entries(perAccount)) {
            // 1) Merge measuresDatatable (concat rows per date)
            for (const [date, rows] of Object.entries(data.measuresDatatable)) {
            (out.measuresDatatable[date] ??= []).push(...rows);
            }

            // 2) Merge filteredCounters (sum per date + granularity)
            for (const [date, byGranularity] of Object.entries(data.filteredCounters)) {
            const dstByGran = (out.filteredCounters[date] ??= {});
            for (const [granularity, counters] of Object.entries(byGranularity)) {
                dstByGran[granularity] = FinOpsAwsResourcesHelper.mergeCounters(dstByGran[granularity], counters);
            }
            }

            // 3) Merge previousTotalCounters (sum per granularity)
            for (const [granularity, counters] of Object.entries(data.previousTotalCounters)) {
            out.previousTotalCounters[granularity] =
                FinOpsAwsResourcesHelper.mergeCounters(out.previousTotalCounters[granularity], counters);
            }
        }

        // 4) Recompute gaps after all sums
        for (const byGranularity of Object.values(out.filteredCounters)) {
            for (const c of Object.values(byGranularity)) {
                FinOpsAwsResourcesHelper.finalizeGaps(c);
            }
        }
        for (const c of Object.values(out.previousTotalCounters)) {
            FinOpsAwsResourcesHelper.finalizeGaps(c);
        }

        return out;
    }

    static computeHistoricalChartData(
        historicalDatesSelected: string[],
        accountChartsData: ChartsDataByDay,
        filters: Filter[],
        granularity: DateGranularityEnum,
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        translateService: TranslateService,
        dateService: DateService
    ): FinOpsChartHistoricalData {

        // Init sections
        let prediction: boolean = false
        let historicalChartCosts: FinOpsChartHistoricalItem[] = []
        let historicalChartMinCostRatio: number = 0.0
        let historicalChartMinCostValue: number = 0.0

        // 
        let filteredHistoricalChartData: FinOpsChartHistoricalData = {
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            costs: []
        }

        // Calculate charts for selected dates
        historicalDatesSelected.forEach((dateKey) => {
            let filteredResources: FinOpsAwsResource[] = []
            accountChartsData[dateKey].forEach(measure => {
                filteredResources.push(measure)
            })
            filters.forEach(filter => {
                if (filter.key == 'issuesCount') {
                    filteredResources = filteredResources.filter((resource) => resource['issues'].length.toString() === filter.value)
                } else if (filter.key == 'issuesType') {
                    filteredResources = filteredResources.filter((resource) => {
                        let issuesTypeFound = false
                        resource.issues.forEach(issue => {
                            issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                        })
                        return issuesTypeFound
                    })
                } else if (filter.key == 'tag') {
                    filteredResources = filteredResources.filter((resource) => {
                        let tagFound = false
                        Object.entries(resource.tags).forEach((tag: any) => {
                            let tagKey = tag[0]
                            let tagValue = tag[1]
                            filter.value.forEach((filterValue: any) => {
                                let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                            });
                        })
                        return tagFound
                    })
                } else if (filter.key == 'age') {
                    let unknownAge = 99999
                    let filterAge: number = unknownAge
                    if (filter.value == 'today') {
                        filterAge = 0
                    } else if (filter.value.startsWith('last_')) {
                        filterAge = filter.value.substring(5, 7)
                    }
                    filteredResources = filteredResources.filter((resource) => {
                        let ageInRange = false
                        if (filterAge == unknownAge) {
                            ageInRange = resource.age == filterAge
                        } else {
                            ageInRange = resource.age <= filterAge
                        }
                        return ageInRange
                    })
                } else if (filter.key.startsWith('tag_')) {
                    let tagKey = filter.key.substring(4)
                    if (Array.isArray(filter.value)) {
                        filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                    } else {
                        filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                    }
                } else {
                    if (Array.isArray(filter.value)) {
                        filteredResources = filteredResources.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                    } else {
                        filteredResources = filteredResources.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                    }
                }
            });

            // Compute historical chart data for the current date
            let totalCosts: number = 0
            prediction = false
            filteredResources.forEach(resource => {
                totalCosts += Number(resource.costs_counters[granularity].cost)
                prediction = resource.costs_counters[granularity].prediction
            });

            historicalChartCosts.push(
                {
                    date: new Date(dateKey),
                    cost: parseFloat(floorPipe.transform(totalCosts, 2)),
                    dateString: dateService.formatLongDate(dateKey),
                    predictionString: prediction ? translateService.instant("FINOPS.PREDICTION") + "\n\n" : ""
                }
            )
        })

        // Update properties
        filteredHistoricalChartData.minCostRatioString = historicalChartMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(historicalChartMinCostRatio * 100, 2))
        filteredHistoricalChartData.minCostValueString = historicalChartMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(historicalChartMinCostValue, 2))
        filteredHistoricalChartData.prediction = prediction
        filteredHistoricalChartData.costs = historicalChartCosts

        return filteredHistoricalChartData
    }

    static computeAggregatedHistoricalChartData(
        awsConfig: AwsConfig,
        historicalDatesSelected: string[],
        bulkChartsData: ChartsDataByAccount,
        filters: Filter[],
        granularity: DateGranularityEnum,
        floorPipe: FloorPipe,
        translateService: TranslateService,
        dateService: DateService
    ): FinOpsChartHistoricalData {
        let prediction = false;
        const aggregatedCosts: FinOpsChartHistoricalItem[] = [];

        // Iterate over all selected dates
        historicalDatesSelected.forEach((dateKey) => {
            let totalCostForDate = 0;
            let datePrediction = false;

            // Iterate over all accounts
            for (const account of awsConfig.accounts) {
                const accountData = bulkChartsData[account.account_id];
                if (!accountData || !accountData[dateKey]) continue;

                // Apply filters per account
                let filteredResources = [...accountData[dateKey]];
                filters.forEach(filter => {
                    if (filter.key == 'issuesCount') {
                        filteredResources = filteredResources.filter((resource) => resource['issues'].length.toString() === filter.value)
                    } else if (filter.key == 'issuesType') {
                        filteredResources = filteredResources.filter((resource) => {
                            let issuesTypeFound = false
                            resource.issues.forEach(issue => {
                                issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                            })
                            return issuesTypeFound
                        })
                    } else if (filter.key == 'tag') {
                        filteredResources = filteredResources.filter((resource) => {
                            let tagFound = false
                            Object.entries(resource.tags).forEach((tag: any) => {
                                let tagKey = tag[0]
                                let tagValue = tag[1]
                                filter.value.forEach((filterValue: any) => {
                                    let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                    let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                    tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                                });
                            })
                            return tagFound
                        })
                    } else if (filter.key == 'age') {
                        let unknownAge = 99999
                        let filterAge: number = unknownAge
                        if (filter.value == 'today') {
                            filterAge = 0
                        } else if (filter.value.startsWith('last_')) {
                            filterAge = filter.value.substring(5, 7)
                        }
                        filteredResources = filteredResources.filter((resource) => {
                            let ageInRange = false
                            if (filterAge == unknownAge) {
                                ageInRange = resource.age == filterAge
                            } else {
                                ageInRange = resource.age <= filterAge
                            }
                            return ageInRange
                        })
                    } else if (filter.key.startsWith('tag_')) {
                        let tagKey = filter.key.substring(4)
                        if (Array.isArray(filter.value)) {
                            filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                        } else {
                            filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                        }
                    } else {
                        if (Array.isArray(filter.value)) {
                            filteredResources = filteredResources.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                        } else {
                            filteredResources = filteredResources.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                        }
                    }
                });

                // Sum costs for this date
                filteredResources.forEach((service) => {
                    totalCostForDate += Number(service.costs_counters[granularity].cost);
                    datePrediction = datePrediction || service.costs_counters[granularity].prediction;
                });
            }

            // Add aggregated data for this date
            aggregatedCosts.push({
                date: new Date(dateKey),
                cost: parseFloat(floorPipe.transform(totalCostForDate, 2)),
                dateString: dateService.formatLongDate(dateKey),
                predictionString: datePrediction ? translateService.instant("FINOPS.PREDICTION") + "\n\n" : "",
            });

            prediction = prediction || datePrediction;
        });

        return {
            costs: aggregatedCosts,
            prediction,
            minCostRatioString:"",
            minCostValueString:""
        };
    }

    static computeCompareProjectsChartData(
        accountChartsData: ChartsDataByDay,
        targetDate: string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService
    ): FinOpsChartCompareData {

        // Init agregator
        let filteredResources: FinOpsAwsResource[] = []

        // Init return object
        let filteredCompareProjectChartData: FinOpsChartCompareData = {
            costs:[],
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            dateString: ""
        }

        let compareProjectsMinCostRatio: number = 0.01
        let compareProjectsMinCostValue: number = 0.0
        let compareProjectsChartCosts: FinOpsChartCompareItem[] = []
        let totalCostsProjects: number = 0
        let totalCostsByProjects = new Map<string, number>();
        let prediction = false

        // Retrieve all services from measure
        accountChartsData[targetDate].forEach(measure => {
            filteredResources.push(measure)
        })


        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                filteredResources = filteredResources.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                filteredResources = filteredResources.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'tag') {
                filteredResources = filteredResources.filter((resource) => {
                    let tagFound = false
                    Object.entries(resource.tags).forEach((tag: any) => {
                        let tagKey = tag[0]
                        let tagValue = tag[1]
                        filter.value.forEach((filterValue: any) => {
                            let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                            let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                            tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                        });
                    })
                    return tagFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                filteredResources = filteredResources.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else if (filter.key.startsWith('tag_')) {
                let tagKey = filter.key.substring(4)
                if (Array.isArray(filter.value)) {
                    filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                } else {
                    filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                }
            } else {
                if (Array.isArray(filter.value)) {
                    filteredResources = filteredResources.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                } else {
                    filteredResources = filteredResources.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                }
            }
        });

        // Look at all selected filters, and remove from "filteredResources" not selected resources.
        filteredResources.forEach(resource => {
            let resourceCost = Number(resource.costs_counters[granularity].cost)
            prediction = resource.costs_counters[granularity].prediction
            
            resource.projects.forEach(project => {
                // Compute the type for each resource's project
                let projectCost = totalCostsByProjects.get(project)
                if (projectCost === undefined) {
                    projectCost = 0
                }
                totalCostsProjects += resourceCost
                projectCost += resourceCost
                totalCostsByProjects.set(project, projectCost)
            })
        });

        compareProjectsMinCostValue = totalCostsProjects * compareProjectsMinCostRatio
        totalCostsByProjects.forEach((value: number, key: string) => {
            if (value > compareProjectsMinCostValue) {
                let colorKey = 'charts_color_' + compareProjectsChartCosts.length
                compareProjectsChartCosts.push(
                    {
                        category: key,
                        cost: parseFloat(floorPipe.transform(value, 2)),
                        color: ColorEnum[colorKey as keyof typeof ColorEnum]
                    }
                )
            }
        });

        filteredCompareProjectChartData.minCostRatioString = compareProjectsMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareProjectsMinCostRatio * 100, 2))
        filteredCompareProjectChartData.minCostValueString = compareProjectsMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareProjectsMinCostValue, 2))
        filteredCompareProjectChartData.dateString = dateService.formatLongDate(targetDate)
        filteredCompareProjectChartData.prediction = prediction
        filteredCompareProjectChartData.costs = compareProjectsChartCosts.sort((a, b) => b["cost"] - a["cost"])

        return filteredCompareProjectChartData
    }

    static computeAggregatedCompareProjectsChartData(
        bulkChartsData: ChartsDataByAccount,
        targetDate: string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService
    ): FinOpsChartCompareData {

        // Init aggregation containers
        let totalCostsByProjects = new Map<string, number>()
        let totalCostsProjects = 0
        let prediction = false

        // Iterate over all accounts
        Object.values(bulkChartsData).forEach(accountChartsData => {
            // Defensive: skip if no data for this date
            if (!accountChartsData[targetDate]) return

            // Apply the same filtering logic as computeCompareProjectChartData
            let filteredResources: FinOpsAwsResource[] = [...accountChartsData[targetDate]]

            filters.forEach(filter => {
                if (filter.key == 'issuesCount') {
                    filteredResources = filteredResources.filter((resource) => resource['issues'].length.toString() === filter.value)
                } else if (filter.key == 'issuesType') {
                    filteredResources = filteredResources.filter((resource) => {
                        let issuesTypeFound = false
                        resource.issues.forEach(issue => {
                            issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                        })
                        return issuesTypeFound
                    })
                } else if (filter.key == 'tag') {
                    filteredResources = filteredResources.filter((resource) => {
                        let tagFound = false
                        Object.entries(resource.tags).forEach((tag: any) => {
                            let tagKey = tag[0]
                            let tagValue = tag[1]
                            filter.value.forEach((filterValue: any) => {
                                let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                            });
                        })
                        return tagFound
                    })
                } else if (filter.key == 'age') {
                    let unknownAge = 99999
                    let filterAge: number = unknownAge
                    if (filter.value == 'today') {
                        filterAge = 0
                    } else if (filter.value.startsWith('last_')) {
                        filterAge = filter.value.substring(5, 7)
                    }
                    filteredResources = filteredResources.filter((resource) => {
                        let ageInRange = false
                        if (filterAge == unknownAge) {
                            ageInRange = resource.age == filterAge
                        } else {
                            ageInRange = resource.age <= filterAge
                        }
                        return ageInRange
                    })
                } else if (filter.key.startsWith('tag_')) {
                    let tagKey = filter.key.substring(4)
                    if (Array.isArray(filter.value)) {
                        filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                    } else {
                        filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                    }
                } else {
                    if (Array.isArray(filter.value)) {
                        filteredResources = filteredResources.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                    } else {
                        filteredResources = filteredResources.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                    }
                }
            });

            // Aggregate project costs across all accounts
            filteredResources.forEach(resource => {
                let resourceCost = Number(resource.costs_counters[granularity].cost)
                prediction = prediction || resource.costs_counters[granularity].prediction

                resource.projects.forEach(project => {
                    let projectCost = totalCostsByProjects.get(project) ?? 0
                    totalCostsProjects += resourceCost
                    projectCost += resourceCost
                    totalCostsByProjects.set(project, projectCost)
                })
            })
        })

        // Now compute the chart data
        let compareProjectsMinCostRatio = 0.01
        let compareProjectsMinCostValue = totalCostsProjects * compareProjectsMinCostRatio
        let compareProjectsChartCosts: FinOpsChartCompareItem[] = []

        totalCostsByProjects.forEach((value: number, key: string) => {
            if (value > compareProjectsMinCostValue) {
                let colorKey = 'charts_color_' + compareProjectsChartCosts.length
                compareProjectsChartCosts.push({
                    category: key,
                    cost: parseFloat(floorPipe.transform(value, 2)),
                    color: ColorEnum[colorKey as keyof typeof ColorEnum]
                })
            }
        })

        return {
            minCostRatioString: compareProjectsMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareProjectsMinCostRatio * 100, 2)),
            minCostValueString: compareProjectsMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareProjectsMinCostValue, 2)),
            dateString: dateService.formatLongDate(targetDate),
            prediction,
            costs: compareProjectsChartCosts.sort((a, b) => b.cost - a.cost)
        }
    }

    static computeCompareTypesChartData(
        accountChartsData: ChartsDataByDay,
        targetDate: string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService,
        awsResourceTypeLabelPipe: AwsResourceTypeLabelPipe
    ): FinOpsChartCompareData {

        // Init agregator
        let filteredResources: FinOpsAwsResource[] = []

        // Init return object
        let filteredCompareTypesChartData: FinOpsChartCompareData = {
            costs:[],
            minCostRatioString: "",
            minCostValueString: "",
            prediction: false,
            dateString: ""
        }

        let compareTypesMinCostRatio: number = 0.01
        let compareTypesMinCostValue: number = 0.0
        let compareTypesChartCosts: FinOpsChartCompareItem[] = []
        let totalCostsTypes: number = 0
        let totalCostsByTypes = new Map<string, number>();
        let prediction: boolean = false

        // Retrieve all services from measure
        accountChartsData[targetDate].forEach(measure => {
            filteredResources.push(measure)
        })

        filters.forEach(filter => {
            if (filter.key == 'issuesCount') {
                filteredResources = filteredResources.filter((resource) => resource['issues'].length.toString() === filter.value)
            } else if (filter.key == 'issuesType') {
                filteredResources = filteredResources.filter((resource) => {
                    let issuesTypeFound = false
                    resource.issues.forEach(issue => {
                        issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                    })
                    return issuesTypeFound
                })
            } else if (filter.key == 'tag') {
                filteredResources = filteredResources.filter((resource) => {
                    let tagFound = false
                    Object.entries(resource.tags).forEach((tag: any) => {
                        let tagKey = tag[0]
                        let tagValue = tag[1]
                        filter.value.forEach((filterValue: any) => {
                            let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                            let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                            tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                        });
                    })
                    return tagFound
                })
            } else if (filter.key == 'age') {
                let unknownAge = 99999
                let filterAge: number = unknownAge
                if (filter.value == 'today') {
                    filterAge = 0
                } else if (filter.value.startsWith('last_')) {
                    filterAge = filter.value.substring(5, 7)
                }
                filteredResources = filteredResources.filter((resource) => {
                    let ageInRange = false
                    if (filterAge == unknownAge) {
                        ageInRange = resource.age == filterAge
                    } else {
                        ageInRange = resource.age <= filterAge
                    }
                    return ageInRange
                })
            } else if (filter.key.startsWith('tag_')) {
                let tagKey = filter.key.substring(4)
                if (Array.isArray(filter.value)) {
                    filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                } else {
                    filteredResources = filteredResources.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                }
            } else {
                if (Array.isArray(filter.value)) {
                    filteredResources = filteredResources.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                } else {
                    filteredResources = filteredResources.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                }
            }
        });

        // Look at all selected filters, and remove from "filteredResources" not selected resources.
        filteredResources.forEach(resource => {
            let resourceCost = Number(resource.costs_counters[granularity].cost)
            prediction = resource.costs_counters[granularity].prediction

            // Compute the cost for the resource's type
            let typeCost = totalCostsByTypes.get(resource.type)
            if (typeCost === undefined) {
                typeCost = 0
            }
            totalCostsTypes += resourceCost
            typeCost += resourceCost
            totalCostsByTypes.set(resource.type, typeCost)
        });

        compareTypesMinCostValue = totalCostsTypes * compareTypesMinCostRatio
        totalCostsByTypes.forEach((value: number, key: string) => {
            if (value > compareTypesMinCostValue) {
                let colorKey = 'charts_color_' + compareTypesChartCosts.length
                compareTypesChartCosts.push(
                    {
                        category: awsResourceTypeLabelPipe.transform(key),
                        cost: parseFloat(floorPipe.transform(value, 2)),
                        color: ColorEnum[colorKey as keyof typeof ColorEnum]
                    }
                )
            }
        });

        filteredCompareTypesChartData.minCostRatioString = compareTypesMinCostRatio == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareTypesMinCostRatio * 100, 2))
        filteredCompareTypesChartData.minCostValueString = compareTypesMinCostValue == 0 ? "" : numberFormatPipe.transform(floorPipe.transform(compareTypesMinCostValue, 2))
        filteredCompareTypesChartData.dateString = dateService.formatLongDate(targetDate)
        filteredCompareTypesChartData.prediction = prediction
        filteredCompareTypesChartData.costs = compareTypesChartCosts.sort((a, b) => b["cost"] - a["cost"])

        return filteredCompareTypesChartData
    }

    static computeAggredatedCompareTypesChartData(
        awsConfig: AwsConfig,
        bulkChartsData: ChartsDataByAccount,
        compareDate: string,
        granularity: DateGranularityEnum,
        filters: Filter[],
        floorPipe: FloorPipe,
        numberFormatPipe: FormatNumberPipe,
        dateService: DateService,
        awsResourceTypeLabelPipe: AwsResourceTypeLabelPipe
    ): FinOpsChartCompareData {
        const compareMinCostRatio:number = 0.01; // keep same as single-account helper
        const groupedCosts: Record<string, number> = {};
        let totalCosts = 0;
        let prediction = false;

        // Iterate available accounts
        for (const account of awsConfig.accounts) {
            const rowsForDate = bulkChartsData[account.account_id]?.[compareDate] ?? [];
            if (!rowsForDate.length) continue;

            // Apply filters BEFORE aggregation
            let filtered = [...rowsForDate];
            for (const filter of filters) {
                if (filter.key == 'issuesCount') {
                    filtered = filtered.filter((resource) => resource['issues'].length.toString() === filter.value)
                } else if (filter.key == 'issuesType') {
                    filtered = filtered.filter((resource) => {
                        let issuesTypeFound = false
                        resource.issues.forEach(issue => {
                            issuesTypeFound = issuesTypeFound || filter.value.includes(issue.issue_type)
                        })
                        return issuesTypeFound
                    })
                } else if (filter.key == 'tag') {
                    filtered = filtered.filter((resource) => {
                        let tagFound = false
                        Object.entries(resource.tags).forEach((tag: any) => {
                            let tagKey = tag[0]
                            let tagValue = tag[1]
                            filter.value.forEach((filterValue: any) => {
                                let filterTagKey = filterValue.substring(0, filterValue.indexOf("="))
                                let filterTagValue = filterValue.substring(filterValue.indexOf("=") + 1)
                                tagFound = tagFound || (tagKey == filterTagKey && tagValue == filterTagValue)
                            });
                        })
                        return tagFound
                    })
                } else if (filter.key == 'age') {
                    let unknownAge = 99999
                    let filterAge: number = unknownAge
                    if (filter.value == 'today') {
                        filterAge = 0
                    } else if (filter.value.startsWith('last_')) {
                        filterAge = filter.value.substring(5, 7)
                    }
                    filtered = filtered.filter((resource) => {
                        let ageInRange = false
                        if (filterAge == unknownAge) {
                            ageInRange = resource.age == filterAge
                        } else {
                            ageInRange = resource.age <= filterAge
                        }
                        return ageInRange
                    })
                } else if (filter.key.startsWith('tag_')) {
                    let tagKey = filter.key.substring(4)
                    if (Array.isArray(filter.value)) {
                        filtered = filtered.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && filter.value.includes(pair[1])))
                    } else {
                        filtered = filtered.filter((resource) => Object.entries(resource['tags']).find(pair => pair[0] === tagKey && pair[1] === filter.value))
                    }
                } else {
                    if (Array.isArray(filter.value)) {
                        filtered = filtered.filter((resource) => filter.value.includes(resource[filter.key as keyof FinOpsAwsResource]))
                    } else {
                        filtered = filtered.filter((resource) => resource[filter.key as keyof FinOpsAwsResource] === filter.value)
                    }
                }
            };

            // Sum filtered costs + OR prediction, and group by category
            for (const svc of filtered) {
                const cost = Number(svc.costs_counters[granularity].cost);
                totalCosts += cost;
                prediction = prediction || svc.costs_counters[granularity].prediction;

                const category = awsResourceTypeLabelPipe.transform(svc.type);
                groupedCosts[category] = (groupedCosts[category] ?? 0) + cost;
            }
        }

        // Compute global threshold based on filtered total
        const minCostValue = totalCosts * compareMinCostRatio;

        // Build costs array (optionally sorted for stable coloring)
        const costs: FinOpsChartCompareItem[] = Object.entries(groupedCosts)
            .filter(([, cost]) => cost > minCostValue)
            .sort((a, b) => b[1] - a[1])
            .map(([category, cost], index) => {
                const colorKey = `charts_color_${index}` as keyof typeof ColorEnum;
                return {
                    category,
                    cost: parseFloat(floorPipe.transform(cost, 2)),
                    color: ColorEnum[colorKey],
                };
            });

        return {
            costs,
            minCostRatioString:
            compareMinCostRatio === 0
                ? ""
                : numberFormatPipe.transform(floorPipe.transform(compareMinCostRatio * 100, 2)),
            minCostValueString:
            minCostValue === 0
                ? ""
                : numberFormatPipe.transform(floorPipe.transform(minCostValue, 2)),
            prediction,
            dateString: dateService.formatLongDate(compareDate),
        };
    }

}